import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from 'components/common/PageLayout';
import Typography from 'components/common/Typography';
import ToggleButton from 'components/common/ToggleButton';
import Link from 'components/common/Link';
import ModalHeader from 'components/common/ModalHeader';
import {
  Flex,
  Spinner,
  useModal,
  RectangularButton,
  Box,
  useToastContext,
} from '@resideo/blueprint-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBlocker } from 'react-router-dom';
import { useMfaService } from './MFAService';
import MFAList from './MFAList';
import MFAEnrollmentDialog from './MFAEnrollmentDialog';

const { Description } = PageLayout;
const { Text } = Typography;

const MFA_METHODS_QUERY_KEY = ['mfaMethods'];

const MFAPage: FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const queryClient = useQueryClient();
  const { getMfaMethods, enrollMfa, deleteMfaMethod, resetMfa } =
    useMfaService();

  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [isEnrollmentInProgress, setIsEnrollmentInProgress] = useState(false);
  const [isRefreshingMfaMethods, setIsRefreshingMfaMethods] = useState(false);
  const enrollmentWindowRef = useRef<Window | null>(null);

  const {
    Modal: EnrollModal,
    openModal: openEnrollModal,
    closeModal: closeEnrollModal,
  } = useModal({ newDesign: true });

  const {
    Modal: ResetModal,
    openModal: openResetModal,
    closeModal: closeResetModal,
  } = useModal({ newDesign: true });

  const {
    Modal: RemoveModal,
    openModal: openRemoveModal,
    closeModal: closeRemoveModal,
  } = useModal({ newDesign: true });

  const { data, isLoading, isError } = useQuery({
    queryKey: MFA_METHODS_QUERY_KEY,
    queryFn: getMfaMethods,
    refetchOnWindowFocus: true,
  });

  const invalidateMfaMethods = async () => {
    setIsRefreshingMfaMethods(true);
    try {
      await queryClient.invalidateQueries({ queryKey: MFA_METHODS_QUERY_KEY });
    } finally {
      setIsRefreshingMfaMethods(false);
    }
  };

  const genericErrorToast = () =>
    addToast({ toastType: 'Error', message: t('common.toast.error') });

  const navigationBlocker = useBlocker(isEnrollmentInProgress);

  useEffect(() => {
    if (navigationBlocker.state === 'blocked') {
      navigationBlocker.reset();
    }
  }, [navigationBlocker]);

  useEffect(() => {
    if (!isEnrollmentInProgress) {
      return undefined;
    }

    const preventPageExit = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', preventPageExit);
    return () => window.removeEventListener('beforeunload', preventPageExit);
  }, [isEnrollmentInProgress]);

  const enrollMutation = useMutation({
    mutationFn: enrollMfa,
    onSuccess: ({ ticketUrl }) => {
      const enrollmentWindow = enrollmentWindowRef.current;
      if (!enrollmentWindow || enrollmentWindow.closed) {
        enrollmentWindowRef.current = null;
        setIsEnrollmentInProgress(false);
        genericErrorToast();
        return;
      }

      enrollmentWindow.location.href = ticketUrl;
      const refreshAfterEnrollment = window.setInterval(async () => {
        if (!enrollmentWindow.closed) {
          return;
        }

        window.clearInterval(refreshAfterEnrollment);
        await invalidateMfaMethods();
        setIsEnrollmentInProgress(false);
        enrollmentWindowRef.current = null;
      }, 1000);
    },
    onError: () => {
      enrollmentWindowRef.current?.close();
      enrollmentWindowRef.current = null;
      setIsEnrollmentInProgress(false);
      genericErrorToast();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (authenticationMethodId: string) =>
      deleteMfaMethod(authenticationMethodId),
    onSuccess: () => {
      invalidateMfaMethods();
      addToast({ toastType: 'Success', message: t('common.toast.success') });
    },
    onError: genericErrorToast,
  });

  const resetMutation = useMutation({
    mutationFn: resetMfa,
    onSuccess: () => {
      invalidateMfaMethods();
      addToast({ toastType: 'Success', message: t('common.toast.success') });
    },
    onError: genericErrorToast,
  });

  const methods = data ?? [];
  const hasMfaEnabled = methods.length > 0;
  const isMutating =
    enrollMutation.isPending ||
    deleteMutation.isPending ||
    resetMutation.isPending ||
    isEnrollmentInProgress ||
    isRefreshingMfaMethods;

  const handleToggleChange = () => {
    if (isMutating) {
      return;
    }
    if (hasMfaEnabled) {
      openResetModal();
    } else {
      openEnrollModal();
    }
  };

  const handleRemoveMethod = (authenticationMethodId: string) => {
    setPendingRemoveId(authenticationMethodId);
    openRemoveModal();
  };

  const handleConfirmEnroll = () => {
    const popupWidth = window.screen.availWidth;
    const popupHeight = window.screen.availHeight;
    const popupLeft = Math.max(
      0,
      window.screenX + (window.outerWidth - popupWidth) / 2
    );
    const popupTop = Math.max(
      0,
      window.screenY + (window.outerHeight - popupHeight) / 2
    );
    const enrollmentWindow = window.open(
      '',
      'auth0-mfa-enrollment',
      `location=no,height=${popupHeight},width=${popupWidth},left=${popupLeft},top=${popupTop},scrollbars=yes,status=yes`
    );

    if (!enrollmentWindow) {
      genericErrorToast();
      return;
    }

    enrollmentWindowRef.current = enrollmentWindow;
    closeEnrollModal();
    setIsEnrollmentInProgress(true);
    enrollMutation.mutate();
  };

  const handleConfirmRemove = () => {
    if (pendingRemoveId) {
      closeRemoveModal();
      deleteMutation.mutate(pendingRemoveId);
    }
  };

  const handleConfirmReset = () => {
    closeResetModal();
    resetMutation.mutate();
  };

  return (
    <PageLayout data-test-mfa-page-title title={t('signInSecurity.mfa.title')}>
      <Description>
        <Text data-test-mfa-page-description>
          {t('signInSecurity.mfa.description')}
        </Text>
      </Description>

      {isLoading ||
      isEnrollmentInProgress ||
      deleteMutation.isPending ||
      resetMutation.isPending ||
      isRefreshingMfaMethods ? (
        <Flex
          paddingTop='xLarge'
          justifyContent='center'
          alignItems='center'
          flexDirection='column'>
          <Spinner
            aria-label={t('common.loading')}
            size='xLarge'
            color='primary'
          />
        </Flex>
      ) : (
        <>
          {isError && (
            <Text data-test-mfa-page-error strong='true'>
              {t('common.toast.error')}
            </Text>
          )}

          <Flex
            justifyContent='space-between'
            width='100%'
            maxWidth='800px'
            paddingTop='medium'>
            <Text marginBottom='20px' strong='true'>
              {t('signInSecurity.mfa.toggleLabel')}
            </Text>
            <Box data-test-mfa-page-toggle-btn>
              <ToggleButton
                value={hasMfaEnabled}
                onChange={handleToggleChange}
                disabled={isMutating}
              />
            </Box>
          </Flex>

          {hasMfaEnabled && (
            <MFAList
              methods={methods}
              onRemove={handleRemoveMethod}
              disabled={isMutating}
            />
          )}
        </>
      )}

      <MFAEnrollmentDialog
        Modal={EnrollModal}
        closeModal={closeEnrollModal}
        onConfirm={handleConfirmEnroll}
        isSubmitting={enrollMutation.isPending}
      />

      <ResetModal>
        <ModalHeader
          data-test-mfa-reset-modal-close-btn
          title={t('signInSecurity.mfa.resetConfirm.title')}
          textAlign='center'
          closeModal={resetMutation.isPending ? undefined : closeResetModal}
        />
        <Text
          data-test-mfa-reset-modal-description
          paddingLeft='2rem'
          paddingRight='2rem'
          textAlign='center'>
          {t('signInSecurity.mfa.resetConfirm.description')}
        </Text>
        <Flex flexDirection='column' padding='large'>
          <RectangularButton
            data-test-mfa-reset-modal-confirm-btn
            type='button'
            variant='secondary'
            disabled={resetMutation.isPending}
            onClick={handleConfirmReset}>
            {resetMutation.isPending ? <Spinner /> : t('common.turnOff')}
          </RectangularButton>
          <Flex width='100%' padding={20} justifyContent='center'>
            <Link
              data-test-mfa-reset-modal-cancel-link
              onClick={resetMutation.isPending ? undefined : closeResetModal}>
              {t('common.goBack')}
            </Link>
          </Flex>
        </Flex>
      </ResetModal>

      <RemoveModal>
        <ModalHeader
          data-test-mfa-remove-modal-close-btn
          title={t('signInSecurity.mfa.removeConfirm.title')}
          textAlign='center'
          closeModal={deleteMutation.isPending ? undefined : closeRemoveModal}
        />
        <Text
          data-test-mfa-remove-modal-description
          paddingLeft='2rem'
          paddingRight='2rem'
          textAlign='center'>
          {t('signInSecurity.mfa.removeConfirm.description')}
        </Text>
        <Flex flexDirection='column' padding='large'>
          <RectangularButton
            data-test-mfa-remove-modal-confirm-btn
            type='button'
            variant='secondary'
            disabled={deleteMutation.isPending}
            onClick={handleConfirmRemove}>
            {deleteMutation.isPending ? (
              <Spinner />
            ) : (
              t('signInSecurity.mfa.remove')
            )}
          </RectangularButton>
          <Flex width='100%' padding={20} justifyContent='center'>
            <Link
              data-test-mfa-remove-modal-cancel-link
              onClick={deleteMutation.isPending ? undefined : closeRemoveModal}>
              {t('common.goBack')}
            </Link>
          </Flex>
        </Flex>
      </RemoveModal>
    </PageLayout>
  );
};

export default MFAPage;
